import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  clearSessionStorage,
  isSessionValid,
  loadSession,
  persistSession,
  sessionExpiresAtMs,
  type Session,
} from "@/session/types";

type SessionContextValue = {
  session: Session | null;
  setSession: (s: Session | null) => void;
  logout: (expired?: boolean) => void;
  token: string | null;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  children,
  onExpired,
}: {
  children: ReactNode;
  onExpired?: () => void;
}) {
  const [session, setSessionState] = useState<Session | null>(() => loadSession());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  const logout = useCallback((expired = false) => {
    setSessionState(null);
    clearSessionStorage();
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (expired) onExpiredRef.current?.();
  }, []);

  const setSession = useCallback((s: Session | null) => {
    if (!s || !isSessionValid(s)) {
      setSessionState(null);
      clearSessionStorage();
      return;
    }
    persistSession(s);
    setSessionState(s);
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!isSessionValid(session)) return;
    const ms = Math.max(0, sessionExpiresAtMs(session) - Date.now());
    timerRef.current = setTimeout(() => logout(true), ms + 250);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [session, logout]);

  const value = useMemo(
    () => ({
      session,
      setSession,
      logout,
      token: session?.sessionToken ?? null,
    }),
    [session, setSession, logout]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("session_provider_missing");
  return ctx;
}
